---
title: "Glossar"
slug: glossary
description: "Erklärung der wichtigsten Begriffe in hey circle: GRAI, Standort, Paketstatus, Inventar und Vertrag."
keywords:
  - Glossar
  - GRAI
  - Standort
  - Paketstatus
  - Inventar
  - Vertrag
  - Begriffe
---

## Aktiv

Paketstatus. Ein Paket mit dem Status **Aktiv** ist in normalem Betriebszustand und kann für Sendungen verwendet werden. Das Paket hat noch ausreichend Restlebensdauer (Sendungen oder Tage) übrig. Siehe [Analysen lesen](/app/help?article=viewing-analytics) für die Übersicht nach Status.

## Defekt

Paketstatus. Ein Paket mit dem Status **Defekt** wurde per Defektbericht markiert. Es steht bis zur Klärung durch das hey circle Support-Team nicht für den normalen Betrieb zur Verfügung. Siehe [Defekt melden](/app/help?article=reporting-a-defect).

## End of Life (EOL)

Paketstatus. Ein Paket hat das Ende seiner vertraglichen Nutzungsdauer erreicht — entweder die maximale Anzahl von Sendungen oder die vereinbarte Laufzeit. Das Paket wird aus dem aktiven Inventar entfernt. Abkürzung: EOL.

## Externes Ziel

Ein Empfänger außerhalb Ihrer eigenen internen Standorte, z. B. ein Kunde, ein Partner oder ein Logistikdienstleister. Externe Ziele können von Company Admins und Managern im oberen Menü unter **Einstellungen → Empfänger** angelegt werden. Pakete, die an ein externes Ziel versendet werden, verlassen Ihren internen Bestand, bis sie an einem internen Standort wieder empfangen werden. Siehe [Ersteinrichtung](/app/help?article=initial-setup).

## GRAI

Global Returnable Asset Identifier. Eindeutige 34-stellige Kennung, die auf jedem Hey-Circle-Paket als QR-Code und als Klarschrift aufgedruckt ist. Die GRAI identifiziert ein einzelnes physisches Paket dauerhaft und ermöglicht die lückenlose Verfolgung über alle Standorte und Sendungen hinweg.

## Interner Standort

Ein Standort, der zu Ihrem Unternehmen gehört und an dem Pakete gescannt werden können. Nur interne Standorte erscheinen im Scan-Dropdown. Mindestens ein interner Standort muss angelegt sein, bevor Benutzer mit dem Scannen beginnen können. Siehe [Ersteinrichtung](/app/help?article=initial-setup).

## Inventar

Der aktuelle Bestand aller Pakete in Ihrem Unternehmen. Das Inventar entsteht automatisch: Jeder Scan eines Pakets erzeugt einen Eintrag oder aktualisiert den vorhandenen Eintrag. Es gibt keinen manuellen Schritt zur Inventaranlage. Der aktuelle Bestand ist in der **Bestandsgesundheit** des Analytics-Dashboards einsehbar. Siehe [Analysen lesen](/app/help?article=viewing-analytics).

## Kurz vor EOL

Paketstatus. Ein Paket nähert sich dem Ende seiner Nutzungsdauer — entweder der maximalen Anzahl von Sendungen oder dem Ende der Vertragslaufzeit. Das Paket ist noch verwendbar, sollte aber bald ersetzt werden. Angezeigt als **Kurz vor EOL** in der Bestandsübersicht.

## Paketstatus

hey circle unterscheidet zwei unabhängige Status-Dimensionen eines Pakets.

**Zustand** — der physische Zustand eines Pakets, angezeigt in der Bestandsübersicht und in der **Bestandsgesundheit**:

| Zustand | Bedeutung |
|---|---|
| **Aktiv** | Normaler Betrieb, Paket kann versendet werden |
| **Defekt** | Schadensmeldung liegt vor, Klärung durch Support ausstehend |
| **Kurz vor EOL** | Nähert sich dem Nutzungsende |
| **End of Life** | Nutzungsdauer abgelaufen, aus Inventar entfernt |

**Umlaufstatus** — wo sich ein Paket in seinem Sendungszyklus befindet: **Verfügbar** (im Bestand an einem internen Standort), **Unterwegs** (versendet, noch nicht zurückempfangen) und **Abgeschlossen** (Rechnung beglichen bzw. Zyklus beendet). Siehe [Paket-Lebenszyklus](/app/help?article=package-lifecycle).

## Sendung

Ein einzelner Versandvorgang: ein Paket wird von einem Standort zu einem internen oder externen Ziel gesendet. Jede Sendung erzeugt einen Eintrag in der Aktivitätshistorie des Pakets. Siehe [Pakete senden](/app/help?article=sending-a-package).

## Standort

Sammelbegriff für interne Standorte und externe Ziele in hey circle. Im engeren Sinne bezeichnet "Standort" einen internen Standort (einen Ort, der zu Ihrem Unternehmen gehört und Scan-Berechtigung hat). Externe Ziele sind technisch gesehen separate Entitäten. Siehe **Interner Standort** und **Externes Ziel**.

## Vertrag

Die vertragliche Grundlage für eine Gruppe von Hey-Circle-Paketen. Ein Vertrag definiert die Nutzungsbedingungen, z. B. die maximale Anzahl von Sendungen je Paket oder die Laufzeit. Pakete sind einem Vertrag zugeordnet, und ihr Status (Kurz vor EOL, End of Life) wird relativ zur Vertragsbedingung berechnet.
